/**
 * The employee's first-login welcome (Employee rebuild C1, prototype
 * `#o-welcome`) — five steps, Skip or Plan my week.
 *
 * 🔴 IT REPLACES OnboardingTour FOR THE EMPLOYEE EXPERIENCE, AND WHY. The tour
 * pointed at the admin shell — "Dashboard KPIs", "Press Q" — none of which an
 * employee has, and on a phone it never finished when no target was visible
 * (PENDING.md, 1b21f66f). Every other role keeps the tour (AppLayout chooses).
 *
 * 🔴 "SEEN" IS STORED ON THE SERVER, PER USER. The tour remembered itself in
 * localStorage, so it came back on every new phone, every cleared browser and
 * every private window. Closing this one — Skip, Plan my week, Esc or the
 * backdrop — sends PUT /auth/me {welcome_seen: true}; the server stamps
 * users.welcome_seen_at with its own clock and /auth/me returns it, so it is
 * shown once per person, on whichever device they first sign in on.
 * Shown only when /auth/me says `welcome_seen_at: null` — an absent field (an
 * older API, a stub) is not "never seen", and does not pop a modal.
 *
 * Step 1 is the person's REAL guideline: GET /hybrid/policies/mine (days a
 * week) and their team's preferred days from GET /hybrid/my-schedule. With no
 * policy it says so rather than inventing "3 days a week".
 */
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import FormModal from '@/components/Common/FormModal';
import { apiPut } from '@/utils/api';
import { routeRequestError } from '@/utils/validation';
import { useAuthStore } from '@/stores/authStore';
import { naturalList, titleDay } from '@/components/OfficeDays/officeDays';
import type { User } from '@/types';
import { useMyPolicy, useMyWeekPlan } from './myDayData';

/** "3 days a week, team days Tue & Thu" — or what is true when there is no
 *  minimum or no team days. */
export function guidelineStep(min: number | null | undefined, teamDays: readonly string[],
                              loaded: boolean): string {
  const team = teamDays.length ? `team days ${naturalList(teamDays.map(titleDay))}` : '';
  if (min) {
    return `Your office guideline: ${min} day${min === 1 ? '' : 's'} a week${team ? `, ${team}` : ''}`;
  }
  if (!loaded) return 'Your office guideline: loading…';
  return team ? `Your office guideline: no minimum is set for you; ${team}`
    : 'Your office guideline: no minimum is set for you';
}

export const WELCOME_STEPS = [
  'Plan your week, then book a desk near your team',
  'Check in when you arrive (button or desk QR)',
  'Something broken? Raise a request with a photo',
  "Turn on notifications so you don't lose your desk",
] as const;

export default function EmployeeWelcome() {
  const user = useAuthStore((s) => s.user);
  const [closed, setClosed] = useState(false);
  // The reads behind step 1 are made only when the welcome will show — not
  // on every page load of everyone who has already seen it.
  if (!user || user.welcome_seen_at !== null || closed) return null;
  return <WelcomeModal firstName={user.first_name} onClosed={() => setClosed(true)} />;
}

function WelcomeModal({ firstName, onClosed }: { firstName?: string | null; onClosed: () => void }) {
  const updateUser = useAuthStore((s) => s.updateUser);
  const navigate = useNavigate();
  const policy = useMyPolicy();
  const plan = useMyWeekPlan();

  const markSeen = async () => {
    onClosed();
    try {
      const saved = await apiPut<User>('/auth/me', { welcome_seen: true });
      updateUser({ welcome_seen_at: saved?.welcome_seen_at ?? new Date().toISOString() });
    } catch (e) {
      // Closed for this visit either way. Not saved, it shows once more next
      // time — better than trapping the person in a modal that cannot close.
      toast.error(routeRequestError(e, {})?.message
        ?? 'Could not save that you have seen the welcome; it may show once more.');
    }
  };

  const step1 = guidelineStep(policy.q.data?.policy?.min_office_days,
                              plan.q.data?.team_preferred_days ?? [],
                              !policy.q.isLoading);
  const steps = [step1, ...WELCOME_STEPS];

  return (
    <FormModal
      isOpen
      onClose={() => void markSeen()}
      onSubmit={async () => { await markSeen(); navigate('/office-days'); }}
      title={`Welcome to Spaciora${firstName ? `, ${firstName}` : ''} 👋`}
      submitLabel="Plan my week"
      cancelLabel="Skip"
    >
      <ol className="divide-y divide-line-subtle text-sm text-content" data-testid="employee-welcome">
        {steps.map((s, i) => (
          <li key={i} className="py-2.5"><span className="font-semibold">{i + 1}.</span> {s}</li>
        ))}
      </ol>
    </FormModal>
  );
}
